import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Chip, IconButton, TablePagination, CircularProgress, Tooltip
} from '@mui/material';
import { Cancel, Print } from '@mui/icons-material';
import { API } from '../context/AuthContext';
import { fmt, PAYMENT_METHODS } from '../utils';
import { useSnackbar } from 'notistack';
import { printPayment } from '../components/PrintDocument';

export default function PaymentsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    const { data } = await API.get('/payments', { params: { page: page + 1, limit: 20 } });
    setPayments(data.payments); setTotal(data.total);
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const cancelPayment = async (id) => {
    if (!window.confirm('¿Cancelar este pago? Los saldos de facturas serán revertidos.')) return;
    try {
      await API.patch(`/payments/${id}/cancel`);
      enqueueSnackbar('Pago cancelado', { variant: 'info' });
      fetchPayments();
    } catch (err) {
      enqueueSnackbar('Error al cancelar', { variant: 'error' });
    }
  };

  const handlePrint = async (id) => {
    try {
      const { data } = await API.get(`/payments/${id}`, { headers: { 'Cache-Control': 'no-cache' } });
      printPayment(data);
    } catch {
      enqueueSnackbar('Error al cargar recibo', { variant: 'error' });
    }
  };

  return (
    <Box>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={800} color="#1E1B2E">Pagos</Typography>
        <Typography variant="body2" color="text.secondary">{total} pagos registrados</Typography>
      </Box>

      <Card>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Número</TableCell>
              <TableCell>Fecha</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Facturas</TableCell>
              <TableCell>Método</TableCell>
              <TableCell>Referencia</TableCell>
              <TableCell align="right">Monto</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : payments.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 6, color: 'text.secondary' }}>Sin pagos registrados</TableCell></TableRow>
            ) : payments.map(p => (
              <TableRow key={p._id} hover>
                <TableCell><Typography variant="body2" fontWeight={700} color="primary.main">{p.number}</Typography></TableCell>
                <TableCell><Typography variant="body2">{fmt.date(p.date || p.createdAt)}</Typography></TableCell>
                <TableCell><Typography variant="body2" fontWeight={500}>{p.client?.name}</Typography></TableCell>
                <TableCell>
                  {p.invoices?.map((inv, i) => (
                    <Box key={i}>
                      <Typography variant="caption" fontWeight={600}>{inv.invoice?.number || 'FAC'}</Typography>
                      <Typography variant="caption" color="text.secondary"> · {fmt.currency(inv.amount)}</Typography>
                    </Box>
                  ))}
                </TableCell>
                <TableCell><Typography variant="body2">{PAYMENT_METHODS[p.method] || p.method}</Typography></TableCell>
                <TableCell><Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{p.reference || '-'}</Typography></TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight={800} color="success.main">{fmt.currency(p.amount)}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={p.status === 'aplicado' ? 'Aplicado' : 'Cancelado'}
                    color={p.status === 'aplicado' ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Imprimir recibo">
                    <IconButton size="small" color="primary" onClick={() => handlePrint(p._id)}>
                      <Print fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {p.status === 'aplicado' && (
                    <Tooltip title="Cancelar pago">
                      <IconButton size="small" color="error" onClick={() => cancelPayment(p._id)}>
                        <Cancel fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination component="div" count={total} page={page} rowsPerPage={20}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[20]}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`} />
      </Card>
    </Box>
  );
}
